/**
 * Autolink REVALIDATION for the TipTap `Link` extension.
 *
 * The upstream `autolink` plugin is add-only: when the user types
 * whitespace after a word that reads as a URL it ADDS a link mark, and
 * that is the last time it ever looks at that text. Four facts about the
 * library combine into the bug this plugin exists to fix:
 *
 *   1. Autolink never revalidates. Deleting or retyping characters under
 *      an autolinked run leaves the mark in place with whatever `href` it
 *      was born with.
 *   2. The link mark is INCLUSIVE while autolink is on (`inclusive()`
 *      returns `options.autolink`), so a character typed at the run's end
 *      boundary — or at its start — inherits the mark.
 *   3. Together, 1 + 2 mean `3mote.com` → backspace → `3mote.c` → type `x`
 *      renders `3mote.cx` styled as a link whose href is still
 *      `https://3mote.com`. The text says one thing; the link says another.
 *   4. Not every link is text-derived. `here` → `https://x.co` was set on
 *      purpose (toolbar, paste-over-selection, markdown `[text](url)`), and
 *      editing its text must NOT touch its href. The only marks this plugin
 *      may rewrite are the ones whose href the library itself derived from
 *      the text — recognisable because running the linkifier over the OLD
 *      text reproduces the stored href exactly.
 *
 * So, on every doc-changing transaction batch (skipped when any transaction
 * carries the `preventAutolink` meta the upstream plugin honours too):
 *
 *   - Find the changed ranges the same way autolink does and collect the
 *     contiguous same-href link runs in every textblock they touch.
 *   - Map each run back to the OLD document to read what its text was.
 *     A run whose old text did not linkify to its href is explicit — leave
 *     it alone.
 *   - For a text-derived run, take the whitespace-delimited word around
 *     it in the NEW document. If the linkifier finds exactly one URL in
 *     that word, the mark must cover exactly that match with the match's
 *     href (this both extends `3mote.com` → `3mote.com/pricing` and trims
 *     an inherited trailing comma back off). Zero or several matches
 *     means the word no longer reads as one URL — drop the mark.
 *
 * The plugin converges: a second pass over its own output finds every
 * remaining run already normalised and dispatches nothing, which is what
 * keeps `appendTransaction` from looping. Only steps are ever added — no
 * text is inserted or removed — so the caret and undo history are
 * unaffected, and a run this plugin drops also leaves `storedMarks` so the
 * very next keystroke does not re-inherit it.
 */

import type { MarkType, Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state'
import { combineTransactionSteps, findChildrenInRange, getChangedRanges } from '@tiptap/react'
import { find } from 'linkifyjs'

export interface LinkAutolinkRevalidateOptions {
  /** The `link` mark type of the schema this plugin runs against. */
  type: MarkType
  /** Scheme linkify assumes for scheme-less domains — must match the Link extension's. */
  defaultProtocol: string
}

export const linkAutolinkRevalidatePluginKey = new PluginKey('linkAutolinkRevalidate')

/** A contiguous run of text nodes carrying a link mark with one href. */
interface LinkRun {
  from: number
  to: number
  href: string
}

/** Word boundaries are any Unicode whitespace; non-text nodes are flagged separately. */
const WHITESPACE_RE = /\s/

function hrefOf(node: ProseMirrorNode, type: MarkType): string | null {
  if (!node.isText) return null
  const mark = node.marks.find((m) => m.type === type)
  const href = mark?.attrs.href
  return typeof href === 'string' ? href : null
}

/** Contiguous same-href link runs inside one textblock, in document order. */
function collectLinkRuns(block: ProseMirrorNode, blockPos: number, type: MarkType): LinkRun[] {
  const runs: LinkRun[] = []
  let current: LinkRun | null = null
  block.forEach((child, offset) => {
    const from = blockPos + 1 + offset
    const to = from + child.nodeSize
    const href = hrefOf(child, type)
    if (href === null) {
      current = null
      return
    }
    if (current && current.href === href && current.to === from) {
      current.to = to
      return
    }
    current = { from, to, href }
    runs.push(current)
  })
  return runs
}

/**
 * The text the run had BEFORE this batch of transactions. Prefers the old
 * document's own same-href run overlapping the mapped-back range — that is
 * exact for edits inside or at the edges of an existing link. Falls back
 * to the raw mapped-back text when no such run exists, which is the case
 * for a mark that was created (or had its href changed) in this very
 * batch: the mark's own text is then the honest "old text".
 */
function oldTextOfRun(
  oldDoc: ProseMirrorNode,
  oldFrom: number,
  oldTo: number,
  run: LinkRun,
  type: MarkType,
): string {
  const blocks = findChildrenInRange(oldDoc, { from: oldFrom, to: oldTo }, (n) => n.isTextblock)
  for (const { node, pos } of blocks) {
    for (const oldRun of collectLinkRuns(node, pos, type)) {
      if (oldRun.href !== run.href) continue
      if (oldRun.to < oldFrom || oldRun.from > oldTo) continue
      return oldDoc.textBetween(oldRun.from, oldRun.to, '\n', '￼')
    }
  }
  return oldDoc.textBetween(oldFrom, oldTo, '\n', '￼')
}

/**
 * Did the library derive `href` from `text` by linkifying it as a URL?
 *
 * The `type === 'url'` filter is load-bearing and must stay in step with
 * the re-derivation below, which only ever considers `url` matches.
 * Without it an email run (linkify reports those as `type: 'email'` with
 * a `mailto:` href) passes this gate, finds no `url` match to re-derive
 * from, and falls into the removal branch — stripping the anchor off
 * every email in any paragraph an edit touches, saved ones included.
 * Emails belong to the upstream autolink plugin, not to this one.
 */
function isTextDerived(text: string, href: string, defaultProtocol: string): boolean {
  if (!text) return false
  const link = find(text, { defaultProtocol }).find(
    (l) => l.isLink && l.type === 'url' && l.value === text,
  )
  return link?.href === href
}

/**
 * Whitespace-delimited word around `run.from` inside `block`. Non-text
 * inline nodes (mentions, chips) and text carrying a DIFFERENT link href
 * are boundaries too, so an adjacent explicit link is never swallowed.
 */
function wordAround(
  block: ProseMirrorNode,
  blockPos: number,
  run: LinkRun,
  type: MarkType,
): { from: number; to: number; text: string } {
  const contentStart = blockPos + 1
  // Flatten the block's inline children into one char array with the
  // absolute position of each char, so scanning across text-node
  // boundaries (bold inside a link, for instance) is uniform.
  const chars: { pos: number; ch: string; boundary: boolean }[] = []
  block.forEach((child, offset) => {
    const from = contentStart + offset
    if (!child.isText || !child.text) {
      chars.push({ pos: from, ch: '￼', boundary: true })
      return
    }
    const href = hrefOf(child, type)
    const foreign = href !== null && href !== run.href
    for (let i = 0; i < child.text.length; i += 1) {
      const ch = child.text[i] ?? ''
      chars.push({ pos: from + i, ch, boundary: foreign || WHITESPACE_RE.test(ch) })
    }
  })

  let startIdx = chars.findIndex((c) => c.pos === run.from)
  if (startIdx < 0) return { from: run.from, to: run.to, text: '' }
  let endIdx = startIdx
  while (startIdx > 0 && !(chars[startIdx - 1]?.boundary ?? true)) startIdx -= 1
  while (endIdx < chars.length && !(chars[endIdx]?.boundary ?? true)) endIdx += 1

  const from = chars[startIdx]?.pos ?? run.from
  const to =
    endIdx < chars.length ? (chars[endIdx]?.pos ?? run.to) : contentStart + block.content.size
  const text = chars
    .slice(startIdx, endIdx)
    .map((c) => c.ch)
    .join('')
  return { from, to, text }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

export function linkAutolinkRevalidate(options: LinkAutolinkRevalidateOptions): Plugin {
  const { type, defaultProtocol } = options

  return new Plugin({
    key: linkAutolinkRevalidatePluginKey,
    appendTransaction(transactions, oldState, newState): Transaction | null {
      const docChanged = transactions.some((tr) => tr.docChanged) && !oldState.doc.eq(newState.doc)
      if (!docChanged) return null
      if (transactions.some((tr) => tr.getMeta('preventAutolink'))) return null

      const transform = combineTransactionSteps(oldState.doc, [...transactions])
      const inverted = transform.mapping.invert()
      const oldDoc = oldState.doc
      const newDoc = newState.doc
      const { tr } = newState

      // A textblock can sit under several changed ranges (one per step);
      // visit each once so the same run is not rewritten twice.
      const seenBlocks = new Set<number>()
      for (const { newRange } of getChangedRanges(transform)) {
        const blocks = findChildrenInRange(newDoc, newRange, (n) => n.isTextblock)
        for (const { node: block, pos: blockPos } of blocks) {
          if (seenBlocks.has(blockPos)) continue
          seenBlocks.add(blockPos)

          for (const run of collectLinkRuns(block, blockPos, type)) {
            // Map back with an outward bias so characters deleted from
            // inside the run (`3mote.com` → `3mote.co`) are part of the
            // old text — that is exactly the edit that goes stale.
            const oldFrom = clamp(inverted.map(run.from, -1), 0, oldDoc.content.size)
            const oldTo = clamp(inverted.map(run.to, 1), oldFrom, oldDoc.content.size)
            const oldText = oldTextOfRun(oldDoc, oldFrom, oldTo, run, type)
            if (!isTextDerived(oldText, run.href, defaultProtocol)) continue

            const word = wordAround(block, blockPos, run, type)
            const matches = find(word.text, { defaultProtocol }).filter((m) => m.type === 'url')
            const match = matches.length === 1 ? matches[0] : undefined

            const targetFrom = match ? word.from + match.start : -1
            const targetTo = match ? word.from + match.end : -1
            const alreadyNormalised =
              match !== undefined &&
              targetFrom === run.from &&
              targetTo === run.to &&
              match.href === run.href
            if (alreadyNormalised) continue

            const clearFrom = Math.min(run.from, word.from)
            const clearTo = Math.max(run.to, word.to)
            tr.removeMark(clearFrom, clearTo, type)
            if (match) {
              const existing = newDoc
                .resolve(run.from)
                .nodeAfter?.marks.find((m) => m.type === type)
              tr.addMark(
                targetFrom,
                targetTo,
                type.create({ ...(existing?.attrs ?? {}), href: match.href }),
              )
            }
          }
        }
      }

      if (tr.steps.length === 0) return null

      // A dropped or trimmed run must not come straight back on the next
      // keystroke through a stored mark left over from the inclusive mark.
      const stored = tr.storedMarks ?? newState.storedMarks
      if (stored?.some((m) => m.type === type)) tr.removeStoredMark(type)
      return tr
    },
  })
}
